import React, { useState, useEffect } from 'react';
import { Select, Button, Modal, Form, Input, message, Tag, Space, Tooltip, Switch, InputNumber } from 'antd';
import { PlusOutlined, SettingOutlined, CloudOutlined, EditOutlined, DeleteOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { configAPI } from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;

const StorageConfigSelector = ({ value, onChange, disabled = false }) => {
  const [storageConfigs, setStorageConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isStorageModalOpen, setIsStorageModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [editingStorage, setEditingStorage] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [storageForm] = Form.useForm();

  const storageTypes = [
    { value: 'local', label: 'Local Storage', icon: '💾' },
    { value: 'aws_s3', label: 'AWS S3', icon: '🪣' },
    { value: 'custom_s3', label: 'Custom S3', icon: '🗄️' },
    { value: 'dropbox', label: 'Dropbox', icon: '📦' },
    { value: 'sftp', label: 'SFTP', icon: '🔒' },
    { value: 'ftp', label: 'FTP', icon: '📁' },
    { value: 'webdav', label: 'WebDAV', icon: '🌐' }
  ];

  useEffect(() => {
    fetchStorageConfigs();
  }, []);

  const fetchStorageConfigs = async () => {
    try {
      setLoading(true);
      const response = await configAPI.getStorageConfigurations();
      setStorageConfigs(response.data || []);
    } catch (error) {
      console.error('Error fetching storage configurations:', error);
      message.error('Failed to load storage configurations');
    } finally {
      setLoading(false);
    }
  };

  const openStorageModal = () => {
    setEditingStorage(null);
    storageForm.resetFields();
    setIsStorageModalOpen(true);
  };

  const handleStorageCreate = async (values) => {
    try {
      setLoading(true);
      
      // Build connection config based on storage type
      let connectionConfig = {};
      
      switch (values.storage_type) {
        case 'local':
          connectionConfig = {
            base_path: values.base_path || '/tmp/workflow_storage',
            create_if_missing: values.create_if_missing !== false
          };
          break;
        case 'aws_s3':
          connectionConfig = {
            access_key: values.access_key || '',
            secret_key: values.secret_key || '',
            region: values.region || '',
            bucket_name: values.bucket_name || ''
          };
          break;
        case 'custom_s3':
          connectionConfig = {
            endpoint_url: values.endpoint_url || '',
            access_key: values.access_key || '',
            secret_key: values.secret_key || '',
            bucket_name: values.bucket_name || '',
            region: values.region || 'us-east-1'
          };
          break;
        case 'dropbox':
          connectionConfig = {
            access_token: values.access_token || ''
          };
          break;
        case 'sftp':
          connectionConfig = {
            host: values.host || '',
            port: values.port || 22,
            username: values.username || '',
            password: values.password || '',
            ssh_key_file: values.ssh_key_file || '',
            remote_path: values.remote_path || '/'
          };
          break;
        case 'ftp':
          connectionConfig = {
            host: values.host || '',
            port: values.port || 21,
            username: values.username || '',
            password: values.password || '',
            passive: values.passive !== false,
            secure: values.secure === true,
            remote_path: values.remote_path || '/'
          };
          break;
        case 'webdav':
          connectionConfig = {
            base_url: values.base_url || '',
            username: values.username || '',
            password: values.password || '',
            verify_ssl: values.verify_ssl !== false
          };
          break;
        default:
          connectionConfig = {};
          break;
      }
      
      const storageData = {
        name: values.name,
        storage_type: values.storage_type,
        is_active: values.is_active !== false,
        is_default: values.is_default === true,
        description: values.description || '',
        connection_config: connectionConfig
      };

      const response = await configAPI.createStorageConfiguration(storageData);
      message.success('Storage configuration created successfully');
      
      setIsStorageModalOpen(false);
      storageForm.resetFields();
      
      // Refresh the list
      await fetchStorageConfigs();
      
      // Force refresh of manage modal
      setRefreshKey(prev => prev + 1);
      
      // Auto-select the newly created configuration
      if (onChange) {
        onChange(response.data.id);
      }
      
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to create storage configuration');
      console.error('Error creating storage configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStorageTypeInfo = (storageType) => {
    return storageTypes.find(t => t.value === storageType) || { label: storageType, icon: '🗄️' };
  };

  const openManageModal = () => {
    setIsManageModalOpen(true);
  };

  const handleEdit = (config) => {
    // First close manage modal and reset form
    setIsManageModalOpen(false);
    storageForm.resetFields();
    
    // Set editing storage (this will trigger the useEffect in StorageConfigForm)
    setEditingStorage(config);
    
    // Open storage modal
    setIsStorageModalOpen(true);
  };

  const handleDelete = async (configId) => {
    try {
      await configAPI.deleteStorageConfiguration(configId);
      message.success('Storage configuration deleted successfully');
      await fetchStorageConfigs();
      
      // Force refresh of manage modal
      setRefreshKey(prev => prev + 1);
      
      if (value === configId && onChange) {
        onChange(null);
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to delete storage configuration');
    }
  };

  const testConnection = async (config) => {
    try {
      setTestingConnection(config.id);
      await configAPI.testStorageConfiguration(config.id);
      message.success('Connection test successful');
    } catch (error) {
      message.error(error.response?.data?.error || 'Connection test failed');
    } finally {
      setTestingConnection(null);
    }
  };

  const handleStorageUpdate = async (values) => {
    try {
      setLoading(true);
      
      // Build connection config based on storage type
      let connectionConfig = {};
      
      switch (values.storage_type) {
        case 'local':
          connectionConfig = {
            base_path: values.base_path || '/tmp/workflow_storage',
            create_if_missing: values.create_if_missing !== false
          };
          break;
        case 'aws_s3':
          connectionConfig = {
            access_key: values.access_key || '',
            secret_key: values.secret_key || '',
            region: values.region || '',
            bucket_name: values.bucket_name || ''
          };
          break;
        case 'custom_s3':
          connectionConfig = {
            endpoint_url: values.endpoint_url || '',
            access_key: values.access_key || '',
            secret_key: values.secret_key || '',
            bucket_name: values.bucket_name || '',
            region: values.region || 'us-east-1'
          };
          break;
        case 'dropbox':
          connectionConfig = {
            access_token: values.access_token || ''
          };
          break;
        case 'sftp':
          connectionConfig = {
            host: values.host || '',
            port: values.port || 22,
            username: values.username || '',
            password: values.password || '',
            ssh_key_file: values.ssh_key_file || '',
            remote_path: values.remote_path || '/'
          };
          break;
        case 'ftp':
          connectionConfig = {
            host: values.host || '',
            port: values.port || 21,
            username: values.username || '',
            password: values.password || '',
            passive: values.passive !== false,
            secure: values.secure === true,
            remote_path: values.remote_path || '/'
          };
          break;
        case 'webdav':
          connectionConfig = {
            base_url: values.base_url || '',
            username: values.username || '',
            password: values.password || '',
            verify_ssl: values.verify_ssl !== false
          };
          break;
        default:
          connectionConfig = {};
          break;
      }
      
      const storageData = {
        name: values.name,
        storage_type: values.storage_type,
        is_active: values.is_active !== false,
        is_default: values.is_default === true,
        description: values.description || '',
        connection_config: connectionConfig
      };

      await configAPI.updateStorageConfiguration(editingStorage.id, storageData);
      message.success('Storage configuration updated successfully');
      
      setIsStorageModalOpen(false);
      setEditingStorage(null);
      storageForm.resetFields();
      
      // Refresh the list
      await fetchStorageConfigs();
      
      // Force refresh of manage modal
      setRefreshKey(prev => prev + 1);
      
      // Re-open the manage modal to show updated configurations
      setIsManageModalOpen(true);
      
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to update storage configuration');
      console.error('Error updating storage configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        <Select
          style={{ flex: 1 }}
          placeholder="Select storage configuration"
          value={value}
          onChange={onChange}
          disabled={disabled}
          loading={loading}
          allowClear
        >
          {storageConfigs.map(config => {
            const typeInfo = getStorageTypeInfo(config.storage_type);
            return (
              <Option key={config.id} value={config.id}>
                <Space>
                  <span>{typeInfo.icon}</span>
                  <span>{config.name}</span>
                  {config.is_default && <Tag color="blue" size="small">Default</Tag>}
                  <Tag color="geekblue" size="small">{typeInfo.label}</Tag>
                </Space>
              </Option>
            );
          })}
        </Select>
        <Tooltip title="Create new storage configuration">
          <Button
            icon={<PlusOutlined />}
            onClick={openStorageModal}
            disabled={disabled}
          />
        </Tooltip>
        <Tooltip title="Manage storage configurations">
          <Button
            icon={<SettingOutlined />}
            onClick={openManageModal}
            disabled={disabled}
          />
        </Tooltip>
      </Space.Compact>

      {/* Storage Configuration Modal */}
      <Modal
        title={editingStorage ? "Edit Storage Configuration" : "Create Storage Configuration"}
        open={isStorageModalOpen}
        onCancel={() => {
          setIsStorageModalOpen(false);
          setEditingStorage(null);
          storageForm.resetFields();
        }}
        footer={null}
        width={600}
        destroyOnClose={true}
      >
        <StorageConfigForm
          key={editingStorage ? `edit-${editingStorage.id}` : 'create'}
          form={storageForm}
          storageTypes={storageTypes}
          onFinish={editingStorage ? handleStorageUpdate : handleStorageCreate}
          loading={loading}
          editingStorage={editingStorage}
        />
      </Modal>

      {/* Manage Storage Configurations Modal */}
      <Modal
        title="Manage Storage Configurations"
        open={isManageModalOpen}
        onCancel={() => setIsManageModalOpen(false)}
        footer={null}
        width={800}
        key={`manage-modal-${refreshKey}`}
      >
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setIsManageModalOpen(false);
              openStorageModal();
            }}
          >
            Create New Configuration
          </Button>
        </div>
        
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {storageConfigs.map(config => {
            const typeInfo = getStorageTypeInfo(config.storage_type);
            return (
              <div
                key={config.id}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 12,
                  backgroundColor: config.is_active ? '#fff' : '#f5f5f5'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 16, fontWeight: 500 }}>
                        {typeInfo.icon} {config.name}
                      </span>
                      <Tag color="geekblue" size="small">{typeInfo.label}</Tag>
                      {config.is_default && <Tag color="blue" size="small">Default</Tag>}
                      {!config.is_active && <Tag color="red" size="small">Inactive</Tag>}
                    </div>
                    {config.description && (
                      <div style={{ color: '#666', fontSize: 12, marginBottom: 8 }}>
                        {config.description}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#999' }}>
                      Created: {new Date(config.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Space>
                    <Tooltip title="Test Connection">
                      <Button
                        size="small"
                        icon={<CloudOutlined />}
                        onClick={() => testConnection(config)}
                        loading={testingConnection === config.id}
                      />
                    </Tooltip>
                    <Tooltip title="Edit Configuration">
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(config)}
                      />
                    </Tooltip>
                    <Tooltip title="Delete Configuration">
                      <Button
                        size="small"
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => {
                          Modal.confirm({
                            title: 'Delete Storage Configuration',
                            content: `Are you sure you want to delete "${config.name}"?`,
                            onOk: () => handleDelete(config.id),
                          });
                        }}
                      />
                    </Tooltip>
                  </Space>
                </div>
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
};

// Storage Configuration Form Component
const StorageConfigForm = ({ form, storageTypes, onFinish, loading, editingStorage }) => {
  const [selectedStorageType, setSelectedStorageType] = useState(null);

  // Set storage type and form values when editing storage changes
  useEffect(() => {
    if (editingStorage) {
      // Set storage type first
      setSelectedStorageType(editingStorage.storage_type);
      
      // Set form values immediately, including storage type
      const formValues = {
        name: editingStorage.name,
        storage_type: editingStorage.storage_type,
        is_active: editingStorage.is_active,
        is_default: editingStorage.is_default,
        description: editingStorage.description,
        ...editingStorage.connection_config
      };
      
      // Use setTimeout to ensure the storage type state is updated first
      setTimeout(() => {
        form.setFieldsValue(formValues);
      }, 0);
    } else {
      setSelectedStorageType(null);
      form.resetFields();
    }
  }, [editingStorage, form]);

  const handleStorageTypeChange = (value) => {
    setSelectedStorageType(value);
    // Only clear connection-related fields when storage type changes and we're not editing
    if (!editingStorage) {
      const fieldsToReset = [
        'base_path', 'create_if_missing',
        'access_key', 'secret_key', 'region', 'bucket_name', 'endpoint_url',
        'access_token', 'host', 'port', 'username', 'password', 'ssh_key_file',
        'remote_path', 'passive', 'secure', 'base_url', 'verify_ssl'
      ];
      fieldsToReset.forEach(field => form.setFieldValue(field, undefined));
    }
  };

  const renderConnectionFields = () => {
    if (!selectedStorageType) return null;

    switch (selectedStorageType) {
      case 'local':
        return (
          <>
            <Form.Item
              name="base_path"
              label="Base Path"
              rules={[{ required: true, message: 'Please enter the base storage path' }]}
              extra="Absolute path to the local directory for file storage"
            >
              <Input placeholder="/home/user/workflow_storage" />
            </Form.Item>
            <Form.Item
              name="create_if_missing"
              label="Create Directory if Missing"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
          </>
        );

      case 'aws_s3':
        return (
          <>
            <Form.Item
              name="access_key"
              label="Access Key"
              rules={[{ required: true, message: 'Please enter AWS access key' }]}
            >
              <Input placeholder="AKIA..." />
            </Form.Item>
            <Form.Item
              name="secret_key"
              label="Secret Key"
              rules={[{ required: true, message: 'Please enter AWS secret key' }]}
            >
              <Input.Password 
                placeholder="Enter secret key"
                iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>
            <Form.Item
              name="region"
              label="AWS Region"
              rules={[{ required: true, message: 'Please enter AWS region' }]}
            >
              <Input placeholder="us-east-1" />
            </Form.Item>
            <Form.Item
              name="bucket_name"
              label="Bucket Name"
              rules={[{ required: true, message: 'Please enter bucket name' }]}
            >
              <Input placeholder="my-bucket" />
            </Form.Item>
          </>
        );

      case 'custom_s3':
        return (
          <>
            <Form.Item
              name="endpoint_url"
              label="Endpoint URL"
              rules={[{ required: true, message: 'Please enter S3 endpoint URL' }]}
            >
              <Input placeholder="https://s3.example.com" />
            </Form.Item>
            <Form.Item
              name="access_key"
              label="Access Key"
              rules={[{ required: true, message: 'Please enter access key' }]}
            >
              <Input placeholder="Access key" />
            </Form.Item>
            <Form.Item
              name="secret_key"
              label="Secret Key"
              rules={[{ required: true, message: 'Please enter secret key' }]}
            >
              <Input.Password 
                placeholder="Enter secret key"
                iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>
            <Form.Item
              name="bucket_name"
              label="Bucket Name"
              rules={[{ required: true, message: 'Please enter bucket name' }]}
            >
              <Input placeholder="my-bucket" />
            </Form.Item>
            <Form.Item
              name="region"
              label="Region"
              initialValue="us-east-1"
            >
              <Input placeholder="us-east-1" />
            </Form.Item>
          </>
        );

      case 'dropbox':
        return (
          <Form.Item
            name="access_token"
            label="Access Token"
            rules={[{ required: true, message: 'Please enter Dropbox access token' }]}
          >
            <Input.Password 
              placeholder="Enter Dropbox access token"
              iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>
        );

      case 'sftp':
        return (
          <>
            <Form.Item
              name="host"
              label="Host"
              rules={[{ required: true, message: 'Please enter SFTP host' }]}
            >
              <Input placeholder="sftp.example.com" />
            </Form.Item>
            <Form.Item
              name="port"
              label="Port"
              initialValue={22}
            >
              <InputNumber min={1} max={65535} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true, message: 'Please enter username' }]}
            >
              <Input placeholder="Username" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
            >
              <Input.Password 
                placeholder="Password (leave blank if using SSH key)"
                iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>
            <Form.Item
              name="ssh_key_file"
              label="SSH Key File Path"
            >
              <Input placeholder="/path/to/private/key (optional)" />
            </Form.Item>
            <Form.Item
              name="remote_path"
              label="Remote Path"
              initialValue="/"
            >
              <Input placeholder="/" />
            </Form.Item>
          </>
        );

      case 'ftp':
        return (
          <>
            <Form.Item
              name="host"
              label="Host"
              rules={[{ required: true, message: 'Please enter FTP host' }]}
            >
              <Input placeholder="ftp.example.com" />
            </Form.Item>
            <Form.Item
              name="port"
              label="Port"
              initialValue={21}
            >
              <InputNumber min={1} max={65535} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true, message: 'Please enter username' }]}
            >
              <Input placeholder="Username" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
            >
              <Input.Password 
                placeholder="Password"
                iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>
            <Form.Item
              name="remote_path"
              label="Remote Path"
              initialValue="/"
            >
              <Input placeholder="/" />
            </Form.Item>
            <Form.Item
              name="passive"
              label="Passive Mode"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="secure"
              label="Secure FTP (FTPS)"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch />
            </Form.Item>
          </>
        );

      case 'webdav':
        return (
          <>
            <Form.Item
              name="base_url"
              label="WebDAV URL"
              rules={[{ required: true, message: 'Please enter WebDAV URL' }]}
            >
              <Input placeholder="https://webdav.example.com/remote.php/dav/files/username/" />
            </Form.Item>
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true, message: 'Please enter username' }]}
            >
              <Input placeholder="Username" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter password' }]}
            >
              <Input.Password 
                placeholder="Password"
                iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>
            <Form.Item
              name="verify_ssl"
              label="Verify SSL Certificate"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{
        is_active: true,
        is_default: false,
        port: selectedStorageType === 'sftp' ? 22 : 21,
        remote_path: '/',
        passive: true,
        secure: false,
        verify_ssl: true
      }}
    >
      <Form.Item
        name="name"
        label="Configuration Name"
        rules={[
          { required: true, message: 'Please enter a configuration name' },
          { min: 3, message: 'Name must be at least 3 characters' }
        ]}
      >
        <Input placeholder="e.g., Production S3, Development MinIO" />
      </Form.Item>

      <Form.Item
        name="storage_type"
        label="Storage Type"
        rules={[{ required: true, message: 'Please select a storage type' }]}
      >
        <Select 
          placeholder="Select storage service type"
          onChange={handleStorageTypeChange}
          disabled={!!editingStorage} // Don't allow changing type when editing
        >
          {storageTypes.map(type => (
            <Option key={type.value} value={type.value}>
              <Space>
                <span>{type.icon}</span>
                <span>{type.label}</span>
              </Space>
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="description"
        label="Description"
      >
        <TextArea 
          rows={2} 
          placeholder="Brief description of this storage configuration" 
        />
      </Form.Item>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <Form.Item
          name="is_active"
          label="Active"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="is_default"
          label="Set as Default"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </div>

      {selectedStorageType && (
        <div style={{ 
          border: '1px solid #d9d9d9', 
          borderRadius: 8, 
          padding: 16, 
          marginBottom: 16,
          backgroundColor: '#fafafa'
        }}>
          <h4 style={{ marginBottom: 16, color: '#1890ff' }}>
            Connection Settings
          </h4>
          {renderConnectionFields()}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'end', gap: 8 }}>
        <Button onClick={() => form.resetFields()}>
          Reset
        </Button>
        <Button type="primary" htmlType="submit" loading={loading}>
          {editingStorage ? 'Update Configuration' : 'Create Configuration'}
        </Button>
      </div>
    </Form>
  );
};

export default StorageConfigSelector;
